"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Image from "next/image";
import { referenzen } from "@/app/referenzen/page";

// 🔥 Inhaber Mapping
const owners: Record<string, string> = {
  Tamaris: "Nicole Wetzel",
  "s.Oliver": "Gerhard Schweinfurt",
  Mariage: "Maria Deligiannidou",
  "Romeo & Julia": "Jelena Chatschaturowa",
  Felicissimo: "Frank Mirsberger",
  Testoni: "Busold Schabel",
  "Der Heimwerker": "Herr Bundermann",
  "Grandprix Originals": "Marco Ruf",
  "boutique c'est moi": "Josette Haas-Chevroulet",
  Highstyle: "Mohsan Afzal",
  "Immagine Moda": "Khalid Mahmood",
  Pöttler: "Alois Pöttler",
  "Brautservice Saju": "Sabine Julia Seniavski",
  "Colours & Sons": "Guido Schmitz",
  "Lucky De Luca": "Frau Reuter",
  "Salty Skin": "Claudia Horlin-Smith",
  Amalz: "A.Malz",
  Fashionpoint: "Carlos Fürler",
  "Aga Gottschalk": "Victoria Gottschalk",
  "Vincenzo Alocca": "Frau Wagner",
  Ahmaddy: "Ahmaddy A.",
  Fuchs: "Walter Fuchs",
  Sina: "Herr Ende",
  Kamuflage: "Adrianna Ulan-Wesoly",
  Goodstuff: "Herr Rose",
  Hitzegrad: "Herr Bock",
  Alpensünde: "Patricia Fleischmann",
  "Accessoires & More": "Herr Nickel",
  Almsach: "Herr Sarna",
  "Philomena Christ": "Frau Christ",
  Bergweiss: "Yagiz Kiymaz",
  "Feminin Plus": "Frau Arisoy",
  "Schuhshop 24": "André Kaizers",
  "Joy Edition": "Herr Harfensteller",
  TrachtenWolf: "Andreas Wolf",
  "DT Design": "Herr Ettwig",
  "Emily & Angel": "Vicky Müller",
  Blossom: "Jana",
  Chapati: "Hamid Mohammadi",
  Balke: "Kai Balke",
  Modeln: "Herr Azam",
  "Moda Sende": "Volkan Bingöl",
  "Christoph Renner": "Christoph Renner",
  EURA: "Herr Yu",
  "Logo Mesch": "Herr Mesch",
  Luren: "Frau Bella",
};

// 🔥 Individuelle Referenztexte pro Kunde
const reviewTexts: Record<string, string> = {
  Tamaris:
    "Die Anbindung unserer Filialen läuft dank der neuen Lösung absolut reibungslos.",
  "s.Oliver":
    "Besonders die Bestandsverwaltung hat unsere Prozesse massiv beschleunigt.",
  Mariage:
    "Kompetente Beratung und eine Software, die genau versteht, was wir brauchen.",
  "Romeo & Julia":
    "Hervorragender Support und eine intuitive Bedienung – wir sind begeistert.",
  Felicissimo:
    "Die Effizienz im täglichen Geschäftsablauf hat sich spürbar verbessert.",
  Testoni: "Ein zuverlässiger Partner für unsere digitale Warenwirtschaft.",
  "Der Heimwerker":
    "Endlich eine Lösung, die auch komplexe Lagerbestände einfach im Griff hat.",
  "Grandprix Originals":
    "Die Zusammenarbeit ist unkompliziert, direkt und sehr zielorientiert.",
  "boutique c'est moi":
    "Dank der einfachen Handhabung sparen wir täglich wertvolle Zeit.",
  Highstyle:
    "Moderne Technik für moderne Mode – genau das, was wir gesucht haben.",
  "Immagine Moda":
    "Die Implementierung war schnell und der Service ist erstklassig.",
  Pöttler:
    "Wir schätzen die persönliche Betreuung und die technische Stabilität.",
  "Brautservice Saju":
    "Perfekt auf unsere speziellen Anforderungen im Service abgestimmt.",
  "Colours & Sons":
    "Die Schnittstellen funktionieren einwandfrei – ein echtes Upgrade für uns.",
  "Lucky De Luca":
    "Professionelle Abwicklung von A bis Z. Sehr empfehlenswert.",
  "Salty Skin": "Innovation und Zuverlässigkeit werden hier großgeschrieben.",
  Amalz:
    "Eine enorme Erleichterung für unsere gesamte Buchhaltung und Logistik.",
  Fashionpoint:
    "Das Team reagiert immer sofort auf unsere Anfragen. Top Service!",
  "Aga Gottschalk":
    "Die Lösung ist flexibel und wächst mit unseren Anforderungen mit.",
  "Vincenzo Alocca":
    "Wir haben nun volle Transparenz über alle Verkaufszahlen.",
  Ahmaddy:
    "Ein Gewinn für unsere gesamte Organisation und die tägliche Arbeit.",
  Fuchs: "Kompetenz und Leidenschaft merkt man hier in jedem Projektschritt.",
  Sina: "Die Software ist selbsterklärend und hat eine sehr hohe Akzeptanz bei den Mitarbeitern.",
  Kamuflage:
    "Strategisch wertvolle Unterstützung bei unserer digitalen Transformation.",
  Goodstuff: "Eine Partnerschaft auf Augenhöhe mit messbaren Resultaten.",
  Hitzegrad:
    "Die Stabilität des Systems ist auch bei hoher Last beeindruckend.",
  Alpensünde:
    "Tradition trifft Moderne – die Lösung passt perfekt zu unserem Spirit.",
  "Accessoires & More":
    "Wir konnten unsere Fehlerquote im Versand um fast 90% senken.",
  Almsach: "Die Datenverarbeitung ist blitzschnell und sehr zuverlässig.",
  "Philomena Christ":
    "Ein sehr durchdachtes Konzept, das unseren Workflow optimal unterstützt.",
  Bergweiss: "Beste Entscheidung für unser Unternehmen.",
  "Feminin Plus":
    "Kundenwünsche werden hier ernst genommen und direkt umgesetzt.",
  "Schuhshop 24":
    "Die Shop anbindung ist die beste, mit der wir je gearbeitet haben.",
  "Joy Edition": "Alles aus einer Hand – so macht die Digitalisierung Spaß.",
  TrachtenWolf:
    "Starke Leistung und ein sehr faires Preis-Leistungs-Verhältnis.",
  "DT Design": "Kreative Ansätze und technische Exzellenz perfekt kombiniert.",
  "Emily & Angel":
    "Dank der Software haben wir wieder Zeit für das Wesentliche.",
  Blossom: "Ein tolles Team, das uns immer mit Rat und Tat zur Seite steht.",
  Chapati:
    "Die Flexibilität des Systems hat uns neue Vertriebswege ermöglicht.",
  Balke: "Einfach, schnell und effektiv. Genau so muss Software sein.",
  Modeln: "Hervorragende Benutzeroberfläche und sehr performant.",
  "Moda Sende": "Unsere Bestandsgenauigkeit hat sich massiv verbessert.",
  "Christoph Renner":
    "Ein Partner, auf den man sich in jeder Situation verlassen kann.",
  EURA: "Die globale Vernetzung unserer Standorte klappt nun tadellos.",
  "Logo Mesch":
    "Individuelle Anpassungen wurden schnell und präzise umgesetzt.",
  Luren: "Höchste Qualität und eine sehr angenehme Zusammenarbeit.",
};

const defaultReview =
  "Die Zusammenarbeit verläuft zuverlässig und effizient. Die Lösung unterstützt unseren täglichen Betrieb optimal.";

type Review = {
  id: string;
  text: string;
  name: string;
  company: string;
  logo: any;
};

export default function KundenRezensionenCarousel() {
  const reviews: Review[] = useMemo(() => {
    return referenzen
      .map((r: any, i: number) => ({
        id: `${r.name}-${i}`,
        text: reviewTexts[r.name] || defaultReview,
        name: owners[r.name],
        company: r.name,
        logo: r.image,
      }))
      .filter((r) => r.name && r.name !== "...");
  }, []);

  const slides = useMemo(() => {
    if (!reviews.length) return [];
    return [reviews[reviews.length - 1], ...reviews, reviews[0]];
  }, [reviews]);

  const [idx, setIdx] = useState(1);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const isTransitioning = useRef(false);

  const animateToIdx = (targetIdx: number, withTransition = true) => {
    const el = trackRef.current;
    if (!el) return;
    el.style.transition = withTransition
      ? "transform 520ms cubic-bezier(0.22, 1, 0.36, 1)"
      : "none";
    el.style.transform = `translateX(${-targetIdx * 100}%)`;
  };

  useEffect(() => {
    animateToIdx(idx, true);
  }, [idx]);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    const onEnd = () => {
      const lastIndex = slides.length - 1;
      if (idx === 0) {
        const realLast = reviews.length;
        setIdx(realLast);
        requestAnimationFrame(() => animateToIdx(realLast, false));
      } else if (idx === lastIndex) {
        setIdx(1);
        requestAnimationFrame(() => animateToIdx(1, false));
      }
      isTransitioning.current = false;
    };

    el.addEventListener("transitionend", onEnd);
    return () => el.removeEventListener("transitionend", onEnd);
  }, [idx, slides.length, reviews.length]);

  const next = () => {
    if (isTransitioning.current) return;
    isTransitioning.current = true;
    setIdx((i) => i + 1);
  };

  const prev = () => {
    if (isTransitioning.current) return;
    isTransitioning.current = true;
    setIdx((i) => i - 1);
  };

  const activeDot = useMemo(() => {
    const lastIndex = slides.length - 1;
    if (idx === 0) return reviews.length - 1;
    if (idx === lastIndex) return 0;
    return idx - 1;
  }, [idx, reviews.length, slides.length]);

  const arrowBase =
    "w-11 h-11 rounded-full flex items-center justify-center transition-all duration-300 shadow-md";

  if (!slides.length) return null;

  return (
    <div className="max-w-6xl mx-auto px-4">
      <div className="relative">
        {/* Carousel Container */}
        <div className="relative bg-linear-to-br from-white to-slate-50 rounded-3xl shadow-xl overflow-hidden">
          {/* Navigation Buttons - Hier im Container, damit sie auf Mobile funktionieren */}
          <button
            type="button"
            onClick={prev}
            className={`${arrowBase} absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-20 bg-brand-primary text-white shadow-lg scale-90 md:scale-100 hover:bg-brand-primary-dark`}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <button
            type="button"
            onClick={next}
            className={`${arrowBase} absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-20 bg-brand-primary text-white shadow-lg scale-90 md:scale-100 hover:bg-brand-primary-dark`}
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Der Track, der sich bewegt */}
          <div ref={trackRef} className="flex will-change-transform">
            {slides.map((r, i) => (
              <div
                key={`${r.id}-${i}`}
                className="min-w-full flex justify-center px-8 py-12 md:px-20 md:py-14"
              >
                <div className="max-w-2xl w-full text-center flex flex-col items-center">
                  <div className="mb-6 md:mb-8 bg-white rounded-2xl shadow-sm px-6 py-4 inline-block">
                    <Image
                      src={r.logo}
                      alt={r.company}
                      className="h-10 md:h-12 w-auto object-contain"
                    />
                  </div>

                  <p className="text-lg md:text-2xl leading-relaxed text-slate-700 italic px-4">
                    “{r.text}”
                  </p>

                  <div className="mt-6 md:mt-8">
                    <p className="text-base md:text-lg font-semibold text-slate-900">
                      {r.company}
                    </p>
                    <p className="text-xs md:text-sm text-slate-500">
                      {r.name}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dots Navigation */}
        <div className="mt-10 flex flex-wrap justify-center gap-3 px-4">
          {reviews.map((_, i) => (
            <button
              key={i}
              onClick={() => {
                if (isTransitioning.current) return;
                isTransitioning.current = true;
                setIdx(i + 1);
              }}
              className={`w-2.5 h-2.5 rounded-full transition ${
                i === activeDot ? "bg-brand-primary scale-125" : "bg-gray-300"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
